"use client";
import { getTopAddresses, getTransfers } from "@/lib/getWalletAddresses";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";

type DetailRow = {
  time: string;
  from: string;
  to: string;
  value: string;
};

export interface WalletDetailTableProps {
  symbol: "btc" | "eth" | "sol";
  containerHeight?: string | number;
}

export default function WalletDetailTable({ symbol, containerHeight }: WalletDetailTableProps) {
  const t = useTranslations("Analytics");
  const [wallets, setWallets] = useState<any[]>([]);
  // selectedWallet holds the selected entityId (string) or undefined
  const [selectedWallet, setSelectedWallet] = useState<string | undefined>(undefined);
  const [data, setData] = useState<DetailRow[]>([]);
  const [totalTransfers, setTotalTransfers] = useState<number>(0);
  // internal pagination
  const rowsPerPage = 9;
  const [pageIndex, setPageIndex] = useState(0); // 0-based

  useEffect(() => {
    // clamp pageIndex if total shrink
    const maxPage = Math.max(0, Math.ceil(totalTransfers / rowsPerPage) - 1);
    if (pageIndex > maxPage) setPageIndex(maxPage);
  }, [totalTransfers, rowsPerPage, pageIndex]);

  // data holds current page rows (fetched from server)
  const displayRows = data;
  const emptyCount = Math.max(0, rowsPerPage - displayRows.length);
  const internalTotalPages = Math.max(1, Math.ceil(totalTransfers / rowsPerPage));

  // refs & drag state
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef(false);
  const startX = useRef(0);
  const startLeft = useRef(0);
  const [showLeftMask, setShowLeftMask] = useState(false);
  const [showRightMask, setShowRightMask] = useState(false);
  const [maskRect, setMaskRect] = useState<{
    top: number;
    left: number;
    height: number;
    width: number;
    scrollLeft: number;
  } | null>(null);
  // whether the selected wallet has a subscription (controls the overlay)
  const [hasSub, setHasSub] = useState<boolean | undefined>(undefined);
  // computed overlay that covers the right side when hasSub === false
  const [subMask, setSubMask] = useState<{
    top: number;
    left: number;
    height: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // effect: observe size/scroll to update masks and overflow
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const updateMasks = () => {
      const left = el.scrollLeft > 4;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
      setShowLeftMask(left);
      setShowRightMask(right);
    };

    const check = () => updateMasks();
    check();

    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener("resize", check);
    const table = el.querySelector("table");
    if (table) ro.observe(table);

    el.addEventListener("scroll", updateMasks);

    const updateRect = () => {
      const root = rootRef.current;
      const sr = scrollRef.current;
      if (!root || !sr) return;
      const rootRect = root.getBoundingClientRect();
      const scrollRect = sr.getBoundingClientRect();
      // try to compute visible tbody area so mask matches table body (including last row)
      const table = sr.querySelector("table");
      let visibleTop = scrollRect.top;
      let visibleHeight = scrollRect.height;

      if (table) {
        const tbody = table.querySelector("tbody") as HTMLElement | null;
        if (tbody) {
          const tbodyRect = tbody.getBoundingClientRect();
          // compute intersection of tbody and scroll container to get visible tbody area
          const intersectTop = Math.max(tbodyRect.top, scrollRect.top);
          const intersectBottom = Math.min(tbodyRect.bottom, scrollRect.bottom);
          const h = Math.max(0, intersectBottom - intersectTop);
          visibleTop = intersectTop;
          visibleHeight = h;
        }
      }

      setMaskRect({
        top: visibleTop - rootRect.top,
        left: scrollRect.left - rootRect.left,
        height: visibleHeight,
        width: scrollRect.width,
        scrollLeft: sr.scrollLeft,
      });
    };
    updateRect();
    const ro2 = new ResizeObserver(updateRect);
    ro2.observe(el);
    ro2.observe(document.body);
    window.addEventListener("resize", updateRect);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
      el.removeEventListener("scroll", updateMasks);
      try {
        ro2.disconnect();
      } catch {}
      window.removeEventListener("resize", updateRect);
    };
  }, [data]);

  async function fetchData() {
    const { rows: entities, hasSub } = await getTopAddresses(symbol);

    const rows = entities.map((e: any) => ({
      entityId: e.entityId,
      name: e.name,
    }));
    setWallets(rows);
    // set default selected wallet to first entityId if available
    if (rows.length > 0) setSelectedWallet(rows[0].entityId);
    // record subscription flag if server provided it
    setHasSub(typeof hasSub === "boolean" ? hasSub : undefined);
  }

  // when switching wallet or symbol, reset to first page
  useEffect(() => {
    if (!selectedWallet) return;
    setPageIndex(0);
  }, [selectedWallet, symbol]);

  // fetch transfers when selectedWallet, symbol, pageIndex or rowsPerPage change
  useEffect(() => {
    if (!selectedWallet) return;
    loadData();
  }, [selectedWallet, symbol, pageIndex, rowsPerPage]);

  async function loadData() {
    if (!selectedWallet) return;
    try {
      const { transfers, total, hasSub } = await getTransfers(
        selectedWallet,
        symbol,
        rowsPerPage,
        pageIndex * rowsPerPage
      );
      const rows: DetailRow[] = transfers.map((t: any) => ({
        time: new Date(t.date).toLocaleString(undefined, {
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }),
        from: t.fromName || (t.fromAddress ? `${t.fromAddress.slice(0, 6)}...${t.fromAddress.slice(-4)}` : ""),
        to: t.toName || (t.toAddress ? `${t.toAddress.slice(0, 6)}...${t.toAddress.slice(-4)}` : ""),
        value: typeof t.historicalUSD === "number" ? `$${Math.round(t.historicalUSD).toLocaleString()}` : "-",
      }));
      setData(rows);
      setTotalTransfers(total ?? 0);
      // update subscription flag from server-side transfers result if provided
      setHasSub(typeof hasSub === "boolean" ? hasSub : hasSub ?? undefined);
    } catch (err) {
      console.error("loadData error", err);
    }
  }

  // compute overlay rectangle to cover right side when there's no subscription
  useEffect(() => {
    if (!maskRect) {
      setSubMask(null);
      return;
    }
    // only show overlay when hasSub === false
    if (hasSub === undefined || hasSub === true) {
      setSubMask(null);
      return;
    }

    const el = scrollRef.current;
    const table = el?.querySelector("table") as HTMLTableElement | null;
    if (!table) {
      setSubMask(null);
      return;
    }

    const ths = table.querySelectorAll("th");

    // If small screen, cover the right half of the visible table area.
    const isSmallScreen = typeof window !== "undefined" ? window.innerWidth < 768 : false;

    // compute tbody position/height to align mask to table body
    const root = rootRef.current;
    if (!root) {
      setSubMask(null);
      return;
    }
    const rootRect = root.getBoundingClientRect();
    const tbody = table.querySelector("tbody") as HTMLElement | null;
    if (!tbody) {
      setSubMask(null);
      return;
    }
    const tbodyRect = tbody.getBoundingClientRect();

    let left: number;
    let width: number;

    if (isSmallScreen) {
      // cover the right half of the visible table area on small screens
      left = maskRect.left + maskRect.width / 2;
      width = Math.max(0, maskRect.width / 2);
    } else {
      // compute widths of Time and From columns (first two th elements)
      const timeW = ths[0]?.getBoundingClientRect().width ?? 0;
      const fromW = ths[1]?.getBoundingClientRect().width ?? 0;
      const sumCols = timeW + fromW;

      // left is outer visible left + sumCols, width is outer width - sumCols
      left = maskRect.left + sumCols;
      width = Math.max(0, maskRect.width - sumCols);
    }

    if (width < 8 || tbodyRect.height < 8) {
      setSubMask(null);
      return;
    }

  // Align top/height to the visible intersection area between tbody and scroll container
  const visibleTop = Math.max(tbodyRect.top, maskRect.top + rootRect.top);
  const visibleBottom = Math.min(tbodyRect.bottom, maskRect.top + rootRect.top + maskRect.height);
  const alignedTop = visibleTop - rootRect.top;
  const alignedHeight = Math.max(0, visibleBottom - visibleTop);
  setSubMask({ top: alignedTop, left, height: alignedHeight, width });
  }, [maskRect, hasSub, data]);

  // pointer drag handlers
  const onPointerDown = (e: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    // if user does not have subscription, disable horizontal drag
    if (hasSub === false) return;
    dragging.current = true;
    startX.current = e.clientX;
    startLeft.current = el.scrollLeft;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const el = scrollRef.current;
    if (!el || !dragging.current) return;
    if (hasSub === false) return;
    const dx = e.clientX - startX.current;
    el.scrollLeft = startLeft.current - dx;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    dragging.current = false;
    try {
      (e.target as Element).releasePointerCapture?.(e.pointerId);
    } catch {}
  };

  return (
    <Box
      ref={rootRef}
      sx={{
        flex: 1,
        position: "relative",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        bgcolor: "background.paperMedium",
        borderRadius: "0.75rem",
        pt: 1,
        overflow: "hidden",
        height: containerHeight,
        width: "100%",
        maxWidth: "100%",
      }}>
      <Box>
        <FormControl sx={{ px: "1rem", py: "0.375rem" }}>
          <Select
            value={selectedWallet ?? ""}
            onChange={(e) => setSelectedWallet((e.target.value as string) || undefined)}
            variant="standard"
            disableUnderline
            IconComponent={() => null}
            renderValue={() => (
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ fontSize: "var(--font-size-s)", fontWeight: 600 }}>
                  {(() => {
                    const found = wallets.find((w) => w.entityId === selectedWallet);
                    return found ? found.name || found.entityId : "";
                  })()}
                </Typography>
                <KeyboardArrowDownIcon sx={{ verticalAlign: "middle", opacity: 0.7, ml: 0.25 }} />
              </Box>
            )}
            MenuProps={{
              PaperProps: {
                sx: (theme) => ({
                  bgcolor: "background.paperExtreme",
                  borderRadius: "0.75rem",
                  p: 1,
                  mt: 1,
                  boxShadow: "none",
                  width: "auto",
                  minWidth: 0,
                }),
                // inline style to override MUI's injected minWidth
                style: { minWidth: 0, width: "auto" },
              },
              MenuListProps: {
                // inline style to override injected styles
                style: { width: "auto", minWidth: 0, display: "inline-block", whiteSpace: "nowrap" },
                sx: { width: "auto", minWidth: 0 },
              },
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              disableScrollLock: true,
            }}
            sx={{
              minWidth: 160,
              "& .MuiSelect-select": { padding: 0, display: "inline-flex", alignItems: "center" },
            }}>
            {wallets.map((w) => (
              <MenuItem
                key={w.entityId}
                value={w.entityId}
                sx={(theme) => ({
                  fontSize: "1rem",
                  py: 1,
                  px: 2,
                  mb: 0.5,
                  borderRadius: "0.75rem",
                  color: "text.primary",
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "flex-start",
                  "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor:
                      w.entityId === selectedWallet
                        ? `${(theme as any).palette.background.hover} !important`
                        : "transparent !important",
                  },
                })}>
                {w.name || w.entityId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box
          ref={scrollRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onWheel={(e: React.WheelEvent) => {
            // prevent horizontal wheel-based scrolling when no subscription
            if (hasSub === false && Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
              e.preventDefault();
            }
          }}
          sx={(theme) => ({
            mt: "1rem",
            position: "relative",
            flex: 1,
            overflowY: "hidden",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            userSelect: "none",
            width: "100%",
            maxWidth: "100%",
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-track": {
              backgroundColor: alpha(theme.palette.background.paper, 0.6),
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: alpha(theme.palette.text.primary, 0.28),
              borderRadius: 8,
              border: `2px solid transparent`,
              backgroundClip: "content-box",
            },
            scrollbarWidth: "thin",
            scrollbarColor: `${alpha(theme.palette.text.primary, 0.28)} ${alpha(theme.palette.background.paper, 0.6)}`,
          })}>
          <Table size="small" stickyHeader sx={{ minWidth: { xs: 760, md: 700 } }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={(theme) => ({
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    px: "0.75rem",
                    py: "0.5rem",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 400,
                    color: "text.secondary",
                    bgcolor: "background.paperMedium",
                    textWrap: "nowrap",
                    textAlign: "left",
                  })}>
                  {t("WalletDetailTable.columns.time")}
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    px: "0.75rem",
                    py: "0.5rem",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 400,
                    color: "text.secondary",
                    bgcolor: "background.paperMedium",
                    textWrap: "nowrap",
                    textAlign: "left",
                  })}>
                  {t("WalletDetailTable.columns.from")}
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    px: "0.75rem",
                    py: "0.5rem",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 400,
                    color: "text.secondary",
                    bgcolor: "background.paperMedium",
                    textWrap: "nowrap",
                    textAlign: "left",
                  })}>
                  {t("WalletDetailTable.columns.to")}
                </TableCell>
                <TableCell
                  sx={(theme) => ({
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    px: "0.75rem",
                    py: "0.5rem",
                    borderTop: `1px solid ${theme.palette.divider}`,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 400,
                    color: "text.secondary",
                    bgcolor: "background.paperMedium",
                    textWrap: "nowrap",
                    textAlign: "left",
                  })}>
                  {t("WalletDetailTable.columns.value")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ "& .MuiTableCell-root": { borderBottom: "none", textAlign: "left" } }}>
              {displayRows.map((r, idx) => (
                <TableRow key={idx} hover sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}>
                  <TableCell sx={{ p: "0.75rem", whiteSpace: "nowrap", textAlign: "left" }}>{r.time}</TableCell>
                  <TableCell sx={{ p: "0.75rem", textAlign: "left" }}>
                    <Typography noWrap title={r.from} fontSize="var(--font-size-xs)">
                      {r.from}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ p: "0.75rem", textAlign: "left" }}>
                    <Typography noWrap title={r.to} fontSize="var(--font-size-xs)">
                      {r.to}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ p: "0.75rem", fontVariantNumeric: "tabular-nums", textAlign: "left" }}>
                    {r.value}
                  </TableCell>
                </TableRow>
              ))}

              {Array.from({ length: emptyCount }).map((_, i) => (
                <TableRow key={`empty-${i}`} sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}>
                  <TableCell sx={{ height: 40, textAlign: "left" }} />
                  <TableCell sx={{ textAlign: "left" }} />
                  <TableCell sx={{ textAlign: "left" }} />
                  <TableCell sx={{ textAlign: "left" }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* pagination: right on md+, center on xs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          mb: "1.5rem",
          mx: "1.25rem",
          justifyContent: { xs: "center", md: "flex-end" },
        }}>
        <IconButton
          size="small"
          onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
          disabled={pageIndex <= 0 || hasSub === false}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 12L6 8L10 4"
              stroke="#74777A"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>

        <Box sx={{ display: "inline-flex", justifyContent: "center", gap: 1 }}>
          {(() => {
            const maxButtons = 6;
            const current = pageIndex + 1;
            const total = internalTotalPages;

            if (total <= maxButtons) {
              return (
                <>
                  {Array.from({ length: total }).map((_, i) => {
                    const pageNum = i + 1;
                    const active = pageNum === current;
                    return (
                      <Box
                        key={pageNum}
                        onClick={() => {
                          if (hasSub === false) return;
                          setPageIndex(pageNum - 1);
                        }}
                        sx={{
                          width: "1.5rem",
                          height: "1.5rem",
                          borderRadius: "0.25rem",
                          display: "grid",
                          placeItems: "center",
                          color: active ? "text.primary" : "text.secondary",
                          bgcolor: active ? "rgba(255,255,255,0.16)" : "transparent",
                          cursor: hasSub === false ? "default" : "pointer",
                          fontSize: "var(--font-size-xs)",
                          opacity: hasSub === false ? 0.5 : 1,
                          pointerEvents: hasSub === false ? "none" : "auto",
                        }}>
                        {pageNum}
                      </Box>
                    );
                  })}
                </>
              );
            }

            // Reserve first and last slots, middle slots count
            const middleCount = maxButtons - 2; // 4 when maxButtons=6
            let start = current - Math.floor(middleCount / 2);
            let end = start + middleCount - 1;

            // clamp to [2, total-1]
            if (start < 2) {
              start = 2;
              end = start + middleCount - 1;
            }
            if (end > total - 1) {
              end = total - 1;
              start = Math.max(2, end - middleCount + 1);
            }

            const items: any[] = [];

            // first page
            items.push(
              <Box
                key={1}
                onClick={() => {
                  if (hasSub === false) return;
                  setPageIndex(0);
                }}
                sx={{
                  width: "1.5rem",
                  height: "1.5rem",
                  borderRadius: "0.25rem",
                  display: "grid",
                  placeItems: "center",
                  color: 1 === current ? "text.primary" : "text.secondary",
                  bgcolor: 1 === current ? "rgba(255,255,255,0.16)" : "transparent",
                  cursor: hasSub === false ? "default" : "pointer",
                  fontSize: "var(--font-size-xs)",
                  opacity: hasSub === false ? 0.5 : 1,
                  pointerEvents: hasSub === false ? "none" : "auto",
                }}>
                1
              </Box>
            );

            if (start > 2)
              items.push(
                <Box key="lead-ellipsis" sx={{ mx: 0.5, opacity: 0.7 }}>
                  …
                </Box>
              );

            for (let pageNum = start; pageNum <= end; pageNum++) {
              const active = pageNum === current;
              items.push(
                <Box
                  key={pageNum}
                  onClick={() => {
                    if (hasSub === false) return;
                    setPageIndex(pageNum - 1);
                  }}
                  sx={{
                    width: "1.5rem",
                    height: "1.5rem",
                    borderRadius: "0.25rem",
                    display: "grid",
                    placeItems: "center",
                    color: active ? "text.primary" : "text.secondary",
                    bgcolor: active ? "rgba(255,255,255,0.16)" : "transparent",
                    cursor: hasSub === false ? "default" : "pointer",
                    fontSize: "var(--font-size-xs)",
                    opacity: hasSub === false ? 0.5 : 1,
                    pointerEvents: hasSub === false ? "none" : "auto",
                  }}>
                  {pageNum}
                </Box>
              );
            }

            if (end < total - 1)
              items.push(
                <Box key="trail-ellipsis" sx={{ mx: 0.5, opacity: 0.7 }}>
                  …
                </Box>
              );

            // last page
            items.push(
              <Box
                key={total}
                onClick={() => {
                  if (hasSub === false) return;
                  setPageIndex(total - 1);
                }}
                sx={{
                  width: "1.5rem",
                  height: "1.5rem",
                  borderRadius: "0.25rem",
                  display: "grid",
                  placeItems: "center",
                  color: total === current ? "text.primary" : "text.secondary",
                  bgcolor: total === current ? "rgba(255,255,255,0.16)" : "transparent",
                  cursor: hasSub === false ? "default" : "pointer",
                  fontSize: "var(--font-size-xs)",
                  opacity: hasSub === false ? 0.5 : 1,
                  pointerEvents: hasSub === false ? "none" : "auto",
                }}>
                {total}
              </Box>
            );

            return <>{items}</>;
          })()}
        </Box>

        <IconButton
          size="small"
          onClick={() => setPageIndex((p) => Math.min(internalTotalPages - 1, p + 1))}
          disabled={pageIndex >= internalTotalPages - 1 || hasSub === false}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 12L10 8L6 4"
              stroke="#74777A"
              strokeWidth="1.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </IconButton>
      </Box>

      {/* masks rendered on root so they don't move with horizontal scroll */}
      {maskRect && showLeftMask && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            pointerEvents: "none",
            zIndex: 40,
            transition: "opacity 180ms ease",
            // use paperMedium to match light/dark theme
            background: `linear-gradient(to right, ${alpha((theme as any).palette.background.paperMedium, 1)}, ${alpha(
              (theme as any).palette.background.paperMedium,
              0
            )})`,
          })}
          style={{ top: `${maskRect.top}px`, left: `${maskRect.left}px`, height: `${maskRect.height}px`, width: 48 }}
        />
      )}
      {maskRect && showRightMask && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            pointerEvents: "none",
            zIndex: 40,
            transition: "opacity 180ms ease",
            // use paperMedium to match light/dark theme
            background: `linear-gradient(to left, ${alpha((theme as any).palette.background.paperMedium, 1)}, ${alpha(
              (theme as any).palette.background.paperMedium,
              0
            )})`,
          })}
          style={{
            top: `${maskRect.top}px`,
            left: `${maskRect.left + (maskRect.width - 48)}px`,
            height: `${maskRect.height}px`,
            width: 48,
          }}
        />
      )}
      {/* subscription overlay when user has no sub: covers right side of table body */}
      {subMask && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            pointerEvents: "auto",
            zIndex: 50,
            transition: "opacity 180ms ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: theme.palette.text.primary,
            fontSize: "0.95rem",
            fontWeight: 600,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            // use paperMedium so mask matches light/dark theme
            background: `linear-gradient(270deg, ${alpha(
              (theme as any).palette.background.paperMedium,
              1
            )} 20%, ${alpha((theme as any).palette.background.paperMedium, 0)} 100%)`,
          })}
          style={{
            top: `${subMask.top}px`,
            left: `${subMask.left}px`,
            height: `${subMask.height}px`,
            width: subMask.width,
          }}>
          <Box sx={{ textAlign: "center", fontSize: "var(--font-size-xs)", fontWeight: 400, width: "11rem" }}>
            {t("WalletDetailTable.mask.hint")}
          </Box>
        </Box>
      )}
    </Box>
  );
}
